import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, API } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { ArrowLeft, UploadSimple, CheckCircle, Warning, ArrowRight, Eye, ArrowsClockwise } from "@phosphor-icons/react";

const EUR = (n) => (typeof n === "number" ? n.toLocaleString("es-ES", { style: "currency", currency: "EUR" }) : "—");

export default function PortalSubidaMasiva() {
    const [jobs, setJobs] = useState([]);
    const [selectedJob, setSelectedJob] = useState(null);
    const [facturas, setFacturas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [empresasCount, setEmpresasCount] = useState(0);
    const [filter, setFilter] = useState("all"); // all | unassigned | intragrupo
    const fileRef = useRef(null);

    async function loadJobs() {
        setLoading(true);
        try {
            const { data } = await api.get("/asesor/masiva/jobs");
            setJobs(data.items || []);
            if (data.items?.length && !selectedJob) setSelectedJob(data.items[0]);
            // También cargar num empresas para saber si puede subir
            const c = await api.get("/asesor/clientes");
            const totalEmps = (c.data.items || []).reduce((s, x) => s + (x.facturas_count || 0), 0);
            setEmpresasCount(totalEmps); // no exacto pero suficiente
        } catch (e) {
            setError(formatApiError(e, "Error al cargar"));
        } finally {
            setLoading(false);
        }
    }

    async function loadFacturas(jobId) {
        try {
            const { data } = await api.get(`/asesor/masiva/jobs/${jobId}/facturas`);
            setFacturas(data.items || []);
        } catch (e) {
            setError(formatApiError(e, "Error al cargar facturas"));
        }
    }

    useEffect(() => { loadJobs(); }, []);
    useEffect(() => { if (selectedJob?.id) loadFacturas(selectedJob.id); /* eslint-disable-line */ }, [selectedJob?.id]);

    // Polling si está running — actualiza cada 1.5s
    useEffect(() => {
        if (!selectedJob?.id) return;
        if (selectedJob.status !== "running" && selectedJob.status !== "queued") return;
        const t = setInterval(async () => {
            try {
                const { data } = await api.get(`/asesor/masiva/jobs/${selectedJob.id}`);
                setSelectedJob(data);
                if (data.status === "done" || data.status === "crashed" || data.status === "cancelled") {
                    clearInterval(t);
                    await loadFacturas(data.id);
                    await loadJobs();
                }
            } catch {}
        }, 1500);
        return () => clearInterval(t);
    }, [selectedJob?.id, selectedJob?.status]);

    async function onFile(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        setError("");
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        try {
            const { data } = await api.post("/asesor/masiva/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            await loadJobs();
            // Seleccionar el nuevo job
            const { data: newJob } = await api.get(`/asesor/masiva/jobs/${data.job_id}`);
            setSelectedJob(newJob);
        } catch (err) {
            setError(formatApiError(err, "Error al subir"));
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    }

    async function reclasificar() {
        if (!selectedJob?.id) return;
        if (!confirm("Volveremos a intentar clasificar las facturas del saco. Útil si has añadido nuevas empresas con CIF después de subir este ZIP.")) return;
        try {
            const { data } = await api.post(`/asesor/masiva/jobs/${selectedJob.id}/reclasificar`);
            alert(`Reasignadas: ${data.reasignadas}. Todavía sin asignar: ${data.restan_sin_asignar}.`);
            const { data: j } = await api.get(`/asesor/masiva/jobs/${selectedJob.id}`);
            setSelectedJob(j);
            await loadFacturas(selectedJob.id);
        } catch (e) {
            alert(formatApiError(e, "Error al reclasificar"));
        }
    }

    const [reprocJob, setReprocJob] = useState(null);

    async function reprocesarSaco() {
        const unassigned = facturas.filter((f) => f.match_reason === "sin_match");
        if (unassigned.length === 0) return alert("No hay facturas en el saco para reprocesar.");
        if (!confirm(`¿Reprocesar con la IA mejorada las ${unassigned.length} facturas del saco? Puede tardar varios minutos. Los datos se sobreescribirán con la nueva lectura.`)) return;
        // Agrupar por cliente_id (el saco es un cliente virtual)
        const byCliente = {};
        for (const f of unassigned) {
            if (!byCliente[f.cliente_id]) byCliente[f.cliente_id] = [];
            byCliente[f.cliente_id].push(f.id);
        }
        try {
            let totalLanzado = 0;
            let job = null;
            for (const [cid, ids] of Object.entries(byCliente)) {
                const { data } = await api.post(`/asesor/clientes/${cid}/reprocess`, { ids });
                if (data.queued) {
                    totalLanzado += data.total;
                    job = { job_id: data.job_id, total: data.total, processed: 0, cliente_id: cid };
                }
            }
            if (job) {
                setReprocJob(job);
                alert(`Reprocesado lanzado (${totalLanzado} facturas). Se irán actualizando abajo mientras avanza.`);
            } else {
                alert("Ninguna factura del saco tiene su PDF original guardado. No se pueden reprocesar.");
            }
        } catch (e) {
            alert(formatApiError(e, "Error al lanzar reprocesado"));
        }
    }

    // Polling del reprocesado
    useEffect(() => {
        if (!reprocJob?.job_id || reprocJob.status === "done") return;
        const t = setInterval(async () => {
            try {
                const { data } = await api.get(`/asesor/reproc-jobs/${reprocJob.job_id}`);
                setReprocJob({ ...reprocJob, ...data });
                if (data.status === "done") {
                    clearInterval(t);
                    // Refrescar facturas y auto-reclasificar el saco
                    if (selectedJob?.id) {
                        await loadFacturas(selectedJob.id);
                        try {
                            const r = await api.post(`/asesor/masiva/jobs/${selectedJob.id}/reclasificar`);
                            const { data: j } = await api.get(`/asesor/masiva/jobs/${selectedJob.id}`);
                            setSelectedJob(j);
                            await loadFacturas(selectedJob.id);
                            if (r.data.reasignadas > 0) {
                                alert(`Reprocesado terminado. Además se reasignaron ${r.data.reasignadas} facturas al detectar nuevos CIFs válidos.`);
                            }
                        } catch {}
                    }
                }
            } catch {}
        }, 3000);
        return () => clearInterval(t);
    }, [reprocJob?.job_id, reprocJob?.status]); // eslint-disable-line

    const visibles = useMemo(() => {
        if (filter === "unassigned") return facturas.filter((f) => f.match_reason === "sin_match");
        if (filter === "intragrupo") return facturas.filter((f) => f.match_reason === "intragrupo");
        return facturas;
    }, [facturas, filter]);

    const stats = useMemo(() => ({
        total: facturas.length,
        assigned: facturas.filter((f) => f.match_reason && f.match_reason !== "sin_match").length,
        unassigned: facturas.filter((f) => f.match_reason === "sin_match").length,
        intragrupo: facturas.filter((f) => f.match_reason === "intragrupo").length,
    }), [facturas]);

    // CIFs únicos detectados en las facturas del saco (emisor y receptor, agrupados)
    const cifsSaco = useMemo(() => {
        const map = new Map();
        for (const f of facturas) {
            if (f.match_reason !== "sin_match") continue;
            for (const key of ["emisor", "receptor"]) {
                const cif = (f[`${key}_cif`] || "").trim();
                const nombre = (f[`${key}_nombre`] || "").trim();
                if (!cif) continue;
                if (!map.has(cif)) map.set(cif, { cif, nombre, count: 0, es_emisor: 0, es_receptor: 0 });
                const item = map.get(cif);
                item.count += 1;
                if (key === "emisor") item.es_emisor += 1; else item.es_receptor += 1;
            }
        }
        return [...map.values()].sort((a, b) => b.count - a.count);
    }, [facturas]);

    return (
        <div className="min-h-screen bg-[var(--gf-bg)]">
            <Navbar />
            <div className="max-w-7xl mx-auto p-6 md:p-10">
                <Link to="/portal-asesor" className="inline-flex items-center gap-1 text-sm text-[var(--gf-text-secondary)] hover:text-[var(--gf-navy)] mb-4" data-testid="link-back">
                    <ArrowLeft size={14} /> Volver al Portal
                </Link>

                <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
                    <div>
                        <div className="text-[10px] tracking-widest uppercase text-[var(--gf-text-muted)]">Portal del Asesor</div>
                        <h1 className="serif text-3xl md:text-4xl text-[var(--gf-navy)]">Subida Masiva</h1>
                        <p className="text-sm text-[var(--gf-text-secondary)] max-w-3xl">
                            Sube un ZIP con facturas mezcladas de <b>todos tus clientes</b>. La máquina detecta el CIF de emisor y receptor de cada una y las reparte automáticamente a la empresa que le corresponde. Las que no case, quedan en un <b>saco</b> para que las coloques a mano.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <input ref={fileRef} type="file" accept=".zip" onChange={onFile} className="hidden" data-testid="input-zip-masiva" />
                        <button
                            onClick={() => fileRef.current?.click()}
                            className="btn-gold inline-flex items-center gap-2"
                            disabled={uploading}
                            data-testid="btn-upload-masiva"
                        >
                            <UploadSimple size={16} weight="bold" />
                            {uploading ? "Subiendo…" : "Subir ZIP masivo"}
                        </button>
                    </div>
                </div>

                {error && <div className="bg-red-50 border border-red-200 text-red-800 text-sm p-3 mb-4" data-testid="error-banner">{error}</div>}

                <div className="grid md:grid-cols-[240px_1fr] gap-6">
                    {/* Sidebar de jobs */}
                    <div className="gf-card p-3">
                        <h3 className="serif text-sm text-[var(--gf-navy)] mb-2">Subidas masivas</h3>
                        {jobs.length === 0 ? (
                            <p className="text-xs text-[var(--gf-text-muted)]">Aún no has subido ningún ZIP masivo.</p>
                        ) : (
                            <div className="space-y-1" data-testid="jobs-list">
                                {jobs.map((j) => (
                                    <button
                                        key={j.id}
                                        onClick={() => setSelectedJob(j)}
                                        className={`w-full text-left px-2 py-1.5 text-xs border transition ${selectedJob?.id === j.id ? "bg-[var(--gf-navy)] text-white border-[var(--gf-navy)]" : "bg-white border-[var(--gf-border)] hover:border-[var(--gf-navy)]"}`}
                                        data-testid={`job-${j.id}`}
                                    >
                                        <div className="font-mono truncate">{j.filename}</div>
                                        <div className="opacity-80 mt-0.5">
                                            {j.status === "done" ? "✓" : j.status === "running" ? "…" : j.status === "queued" ? "…" : "!"}
                                            {" "}{j.processed || 0}/{j.total || 0} · asig {j.assigned || 0}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Detalle del job */}
                    <div className="space-y-4">
                        {!selectedJob ? (
                            <div className="gf-card text-center py-10 text-[var(--gf-text-secondary)]">
                                Sube tu primer ZIP masivo para empezar. Ten tus clientes/empresas creados con su CIF para que la máquina las case.
                            </div>
                        ) : (
                            <>
                                {/* Métricas */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <StatCard label="Total procesadas" value={selectedJob.processed || 0} sub={`de ${selectedJob.total || 0}`} testid="stat-total" />
                                    <StatCard label="Asignadas por CIF" value={selectedJob.assigned || 0} highlight="green" testid="stat-assigned" />
                                    <StatCard label="En el saco" value={selectedJob.unassigned || 0} highlight={selectedJob.unassigned ? "amber" : ""} testid="stat-unassigned" />
                                    <StatCard label="Fallidas" value={selectedJob.failed || 0} highlight={selectedJob.failed ? "red" : ""} testid="stat-failed" />
                                </div>

                                {/* Progreso — sticky, muy visible, actualiza cada 1.5s */}
                                {(selectedJob.status === "running" || selectedJob.status === "queued") && (
                                    <div className="sticky top-0 z-40 bg-[var(--gf-navy)] text-white shadow-xl -mx-6 md:-mx-10 px-6 md:px-10 py-4 border-b-2 border-[var(--gf-gold)]" data-testid="progress-sticky">
                                        <div className="max-w-6xl mx-auto">
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <ArrowsClockwise size={24} weight="bold" className="animate-spin text-[var(--gf-gold)]" />
                                                <div className="flex-1 min-w-[240px]">
                                                    <div className="text-sm font-semibold uppercase tracking-wide">
                                                        {selectedJob.status === "queued" ? "En cola…" : "Procesando facturas"} · {selectedJob.processed || 0} / {selectedJob.total || "?"}
                                                    </div>
                                                    <div className="text-xs opacity-90 mt-0.5">
                                                        <span className="text-emerald-300">Asignadas: <b>{selectedJob.assigned || 0}</b></span>
                                                        {" · "}
                                                        <span className="text-amber-200">Al saco: <b>{selectedJob.unassigned || 0}</b></span>
                                                        {selectedJob.failed > 0 && <> · <span className="text-red-300">Fallidas: <b>{selectedJob.failed}</b></span></>}
                                                        {selectedJob.last_file && <> · <span className="opacity-70 truncate">último: {selectedJob.last_file.split('/').pop()?.slice(0, 40)}</span></>}
                                                    </div>
                                                </div>
                                                <div className="mono text-2xl font-bold text-[var(--gf-gold)] whitespace-nowrap">
                                                    {selectedJob.total ? `${Math.round((selectedJob.processed / selectedJob.total) * 100)}%` : "0%"}
                                                </div>
                                            </div>
                                            <div className="h-3 bg-white/20 mt-3 overflow-hidden">
                                                <div className="h-full bg-[var(--gf-gold)] transition-all duration-500" style={{ width: selectedJob.total ? `${Math.round((selectedJob.processed / selectedJob.total) * 100)}%` : "0%" }} />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {selectedJob.status === "done" && (
                                    <div className="gf-card bg-emerald-50 border-emerald-300 border-2" data-testid="done-banner">
                                        <div className="flex items-center justify-between flex-wrap gap-3">
                                            <div className="inline-flex items-center gap-3">
                                                <CheckCircle size={32} weight="fill" className="text-emerald-600" />
                                                <div>
                                                    <div className="serif text-xl text-[var(--gf-navy)] font-semibold">✅ Proceso terminado</div>
                                                    <div className="text-sm text-[var(--gf-text-secondary)] mt-1">
                                                        {selectedJob.processed || 0} facturas procesadas ·
                                                        <span className="text-emerald-700 font-semibold"> {selectedJob.assigned || 0} asignadas por CIF</span>
                                                        {selectedJob.unassigned > 0 && <span className="text-amber-700 font-semibold"> · {selectedJob.unassigned} al saco</span>}
                                                        {selectedJob.failed > 0 && <span className="text-red-700 font-semibold"> · {selectedJob.failed} fallidas</span>}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {selectedJob.unassigned > 0 && (
                                                    <>
                                                        <button onClick={reprocesarSaco} className="btn-gold text-xs inline-flex items-center gap-1" data-testid="btn-reproc-saco">
                                                            <ArrowsClockwise size={12} weight="bold" /> Reprocesar todo el saco con IA
                                                        </button>
                                                        <button onClick={reclasificar} className="btn-outline text-xs inline-flex items-center gap-1" data-testid="btn-reclasificar">
                                                            <ArrowsClockwise size={12} /> Reintentar clasificar
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {reprocJob && reprocJob.status !== "done" && (
                                    <div className="gf-card bg-blue-50 border-blue-200" data-testid="reproc-progress">
                                        <div className="text-sm text-[var(--gf-navy)] mb-2 inline-flex items-center gap-2">
                                            <ArrowsClockwise size={16} className="animate-spin" />
                                            Reprocesando con IA · {reprocJob.processed || 0} / {reprocJob.total || 0}
                                            {reprocJob.updated ? ` · actualizadas ${reprocJob.updated}` : ""}
                                        </div>
                                        <div className="w-full bg-white h-2">
                                            <div className="bg-[var(--gf-gold)] h-2 transition-all" style={{ width: `${reprocJob.total ? (100 * (reprocJob.processed || 0) / reprocJob.total) : 0}%` }} />
                                        </div>
                                    </div>
                                )}

                                {selectedJob.status === "crashed" && (
                                    <div className="gf-card bg-red-50 border-red-200 text-sm text-red-800">
                                        Error: {selectedJob.error || "el worker se detuvo inesperadamente"}
                                    </div>
                                )}

                                {/* Filtros */}
                                <div className="flex items-center gap-2 flex-wrap">
                                    <button onClick={() => setFilter("all")} className={`text-xs px-3 py-1 border ${filter === "all" ? "bg-[var(--gf-navy)] text-white border-[var(--gf-navy)]" : "bg-white border-[var(--gf-border)] hover:border-[var(--gf-navy)]"}`} data-testid="filter-all">
                                        Todas ({stats.total})
                                    </button>
                                    <button onClick={() => setFilter("unassigned")} className={`text-xs px-3 py-1 border ${filter === "unassigned" ? "bg-amber-500 text-white border-amber-500" : "bg-white border-[var(--gf-border)] hover:border-amber-500"}`} data-testid="filter-unassigned">
                                        En el saco ({stats.unassigned})
                                    </button>
                                    {stats.intragrupo > 0 && (
                                        <button onClick={() => setFilter("intragrupo")} className={`text-xs px-3 py-1 border ${filter === "intragrupo" ? "bg-blue-500 text-white border-blue-500" : "bg-white border-[var(--gf-border)] hover:border-blue-500"}`} data-testid="filter-intragrupo">
                                            Intragrupo ({stats.intragrupo})
                                        </button>
                                    )}
                                </div>

                                {/* Panel de CIFs detectados en el saco (para saber qué empresas crear) */}
                                {filter === "unassigned" && cifsSaco.length > 0 && (
                                    <div className="gf-card bg-amber-50 border-amber-200" data-testid="cifs-saco-panel">
                                        <h3 className="serif text-sm text-[var(--gf-navy)] mb-2 inline-flex items-center gap-2">
                                            <Warning size={16} weight="fill" className="text-amber-700" />
                                            CIFs detectados en el saco ({cifsSaco.length})
                                        </h3>
                                        <p className="text-xs text-[var(--gf-text-secondary)] mb-3">
                                            Estas facturas fueron al saco porque su CIF de emisor o receptor no coincide con ninguno de tus clientes/empresas. Si alguno debería estar en tu maestro, créalo primero y luego pulsa "Reintentar clasificar el saco".
                                        </p>
                                        <div className="grid md:grid-cols-2 gap-2">
                                            {cifsSaco.slice(0, 12).map((c) => (
                                                <div key={c.cif} className="bg-white border border-amber-200 p-2 text-xs" data-testid={`cif-saco-${c.cif}`}>
                                                    <div className="font-mono font-bold text-[var(--gf-navy)]">{c.cif}</div>
                                                    <div className="text-[var(--gf-text-secondary)] truncate" title={c.nombre}>{c.nombre || "(sin nombre)"}</div>
                                                    <div className="text-[10px] text-[var(--gf-text-muted)] mt-1">
                                                        {c.count} apariciones · {c.es_emisor > 0 && `${c.es_emisor} como emisor`}{c.es_emisor > 0 && c.es_receptor > 0 && " · "}{c.es_receptor > 0 && `${c.es_receptor} como receptor`}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        {cifsSaco.length > 12 && (
                                            <div className="text-xs text-[var(--gf-text-muted)] mt-2">Y {cifsSaco.length - 12} más…</div>
                                        )}
                                    </div>
                                )}

                                {/* Tabla */}
                                <div className="gf-card p-0 overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="data-table data-table--compact w-full" data-testid="tabla-masiva">
                                            <thead>
                                                <tr>
                                                    <th>Fecha</th>
                                                    <th>Nº</th>
                                                    <th className="bg-blue-50">EMISOR (nombre)</th>
                                                    <th className="bg-blue-50">CIF emisor</th>
                                                    <th className="bg-emerald-50">RECEPTOR (nombre)</th>
                                                    <th className="bg-emerald-50">CIF receptor</th>
                                                    <th className="text-right">Base</th>
                                                    <th className="text-right">IVA</th>
                                                    <th className="text-right">Total</th>
                                                    <th>Destino asignado</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {visibles.length === 0 && (
                                                    <tr><td colSpan={11} className="text-center py-8 text-[var(--gf-text-secondary)]">
                                                        {filter === "unassigned" ? "🎉 No hay facturas en el saco. Todo clasificado." : "Sin facturas aún."}
                                                    </td></tr>
                                                )}
                                                {visibles.map((f) => {
                                                    const isUnas = f.match_reason === "sin_match";
                                                    const isIntra = f.match_reason === "intragrupo";
                                                    const rowCls = isUnas ? "bg-amber-50" : isIntra ? "bg-blue-50/40" : "";
                                                    return (
                                                        <tr key={f.id} className={rowCls} data-testid={`row-${f.id}`}>
                                                            <td className="mono text-xs">{f.fecha || "—"}</td>
                                                            <td className="mono text-xs">{f.numero || "—"}</td>
                                                            <td className="text-xs">{f.emisor_nombre || "—"}</td>
                                                            <td className="mono text-xs">{f.emisor_cif || "—"}</td>
                                                            <td className="text-xs">{f.receptor_nombre || "—"}</td>
                                                            <td className="mono text-xs">{f.receptor_cif || "—"}</td>
                                                            <td className="text-right mono">{EUR(f.base_imponible)}</td>
                                                            <td className="text-right mono">{EUR(f.cuota_iva)}</td>
                                                            <td className="text-right mono font-semibold">{EUR(f.total)}</td>
                                                            <td>
                                                                {isUnas ? (
                                                                    <span className="inline-flex items-center gap-1 text-amber-800 text-xs font-semibold">
                                                                        <Warning size={12} weight="fill" /> Saco
                                                                    </span>
                                                                ) : (
                                                                    <div className="text-xs">
                                                                        <div className="font-semibold text-[var(--gf-navy)]">{f.empresa_nombre}</div>
                                                                        <div className="text-[10px] text-[var(--gf-text-muted)]">
                                                                            {f.tipo === "emitida" ? "EMITIDA" : "RECIBIDA"}
                                                                            {isIntra && " · intragrupo"}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="text-right">
                                                                {f.cliente_id && (
                                                                    <Link
                                                                        to={`/portal-asesor/${f.cliente_id}`}
                                                                        className="text-xs text-[var(--gf-navy)] hover:underline inline-flex items-center gap-1"
                                                                        data-testid={`link-cliente-${f.id}`}
                                                                    >
                                                                        Ir <ArrowRight size={10} />
                                                                    </Link>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function StatCard({ label, value, sub, highlight, testid }) {
    const bg = highlight === "green" ? "bg-emerald-50 border-emerald-200"
        : highlight === "amber" ? "bg-amber-50 border-amber-200"
        : highlight === "red" ? "bg-red-50 border-red-200"
        : "bg-white border-[var(--gf-border)]";
    return (
        <div className={`border p-3 ${bg}`} data-testid={testid}>
            <div className="text-[10px] tracking-widest uppercase text-[var(--gf-text-muted)]">{label}</div>
            <div className="text-2xl font-bold mt-1 mono">{value}</div>
            {sub && <div className="text-xs text-[var(--gf-text-muted)]">{sub}</div>}
        </div>
    );
}
